<template>
    <div class="consumption-donut position-relative d-flex flex-column h-100" :class="{ 'is-compact': isCompact }">
        <h2 class="kv-title mb-4 chart-title">{{ title }}</h2>

        <div v-if="loading" class="d-flex flex-grow-1 align-items-center chart-content-row py-2">
            <div class="chart-donut-wrapper d-flex align-items-center justify-content-center" style="width: 150px; height: 150px;">
                <KvSkeleton width="125px" height="125px" style="border-radius: 50%;" />
            </div>

            <div v-if="showLegend" class="chart-lateral-legend flex-grow-1 pl-3">
                <div 
                    v-for="i in 3" 
                    :key="i"
                    class="legend-row d-flex align-items-center justify-content-between mb-3"
                >
                    <div class="d-flex align-items-center" style="gap: 8px; width: 60%;">
                        <KvSkeleton width="10px" height="10px" style="border-radius: 50%;" />
                        <KvSkeleton width="80%" height="12px" />
                    </div>
                    <KvSkeleton width="36px" height="12px" />
                </div>
            </div>
        </div>

        <div v-else-if="hasData" class="d-flex flex-grow-1 align-items-center chart-content-row">
            <div class="chart-donut-wrapper">
                <pie-chart 
                    :key="chartKey"
                    :chart-data="chartData" 
                    :options="chartOptions"
                    :style="{ height: '150px', width: '150px', position: 'relative' }"
                    :aria-label="title"
                />
            </div>

            <div v-if="showLegend && legendItems.length > 0" class="chart-lateral-legend flex-grow-1">
                <div 
                    v-for="(item, index) in legendItems" 
                    :key="index"
                    class="legend-row d-flex align-items-center justify-content-between"
                >
                    <div class="d-flex align-items-center legend-info">
                        <span class="legend-dot" :style="{ backgroundColor: item.color }"></span>
                        <KvTooltip :text="item.label" placement="top" class="legend-label-tooltip">
                            <span class="legend-label text-truncate">{{ item.label }}</span>
                        </KvTooltip>
                    </div>
                    <div class="d-flex align-items-center legend-metrics">
                        <span class="legend-percentage">{{ item.percentage }}%</span>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="d-flex justify-content-center align-items-center flex-grow-1 text-muted text-center chart-empty">
            {{ emptyMessage || t('balance-no_data_for_period') }}
        </div>
    </div>
</template>

<script>
import PieChart from "../../components/charts/pieChart.vue";
import KvSkeleton from "../../components/ui/kv-skeleton.vue";
import KvTooltip from "../../components/ui/kv-tooltip.vue";
import FormatMixin from "../mixins/format-mixin.js";
import { escapeHtml, getTooltipEl, hideTooltip, positionTooltip, removeTooltip } from "../../components/charts/html-tooltip.js";

const COMPACT_WIDTH = 460;
const TOOLTIP_ID = 'chartjs-tooltip-donut';

export default {
    name: "ConsumptionDonutChart",
    mixins: [FormatMixin],
    components: {
        PieChart,
        KvSkeleton,
        KvTooltip
    },
    props: {
        title: { type: String, required: true },
        chartData: { type: Object, default: () => ({ labels: [], datasets: [] }) },
        emptyMessage: { type: String, default: "" },
        showLegend: { type: Boolean, default: true },
        loading: { type: Boolean, default: false }
    },
    data() {
        return {
            chartKey: 0,
            isCompact: false
        };
    },
    watch: {
        chartData: {
            deep: true,
            handler() {
                this.chartKey++;
            }
        }
    },
    computed: {
        hasData() {
            const data = this.chartData?.datasets?.[0]?.data;
            return Boolean(data && data.length > 0 && data.some(val => Number(val) > 0));
        },
        legendItems() {
            if (!this.hasData) return [];
            const labels = this.chartData.labels || [];
            const dataset = this.chartData.datasets[0] || {};
            const colors = dataset.backgroundColor || [];
            const data = dataset.data || [];
            const total = data.reduce((acc, val) => acc + Number(val), 0);

            return labels.map((label, index) => {
                const color = Array.isArray(colors) ? colors[index] : colors;
                const value = Number(data[index] || 0);
                const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
                return {
                    label,
                    color: color || '#9CA3AF',
                    value,
                    percentage
                };
            });
        },
        chartOptions() {
            const self = this;
            return {
                responsive: true,
                maintainAspectRatio: false,
                cutoutPercentage: 65,
                legend: { display: false },
                elements: {
                    arc: {
                        borderWidth: 2,
                        borderColor: '#FFFFFF'
                    }
                },
                layout: { padding: 4 },
                tooltips: {
                    enabled: false,
                    custom: function (tooltipModel) {
                        self.renderDonutTooltip(this._chart, tooltipModel);
                    }
                }
            };
        }
    },
    methods: {
        renderDonutTooltip(chart, tooltipModel) {
            const tooltipEl = getTooltipEl(TOOLTIP_ID, {
                borderRadius: '6px',
                padding: '8px 12px',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
                minWidth: '140px',
                maxWidth: '280px',
            });

            if (tooltipModel.opacity === 0) {
                hideTooltip(tooltipEl);
                return;
            }

            if (tooltipModel.dataPoints && tooltipModel.dataPoints.length) {
                tooltipEl.innerHTML = this.buildTooltipHtml(chart, tooltipModel.dataPoints[0]);
            }

            positionTooltip(tooltipEl, chart, tooltipModel, 16);
        },
        buildTooltipHtml(chart, dataPoint) {
            const ds = chart.data.datasets[dataPoint.datasetIndex];
            const index = dataPoint.index;
            const label = chart.data.labels[index] || '';
            const value = Number(ds.data[index] || 0);
            const color = Array.isArray(ds.backgroundColor) ? ds.backgroundColor[index] : (ds.backgroundColor || '#2AAA4C');

            return '<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 4px;">' +
                    `<span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${escapeHtml(color)}; flex-shrink: 0;"></span>` +
                    `<span style="font-weight: 600; color: #111827; font-size: 13px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${escapeHtml(label)}</span>` +
                '</div>' +
                '<div style="display: flex; align-items: baseline; gap: 6px; font-size: 12px; color: #4B5563;">' +
                    `<span style="font-weight: 600; color: #111827;">${this.formatNumber(value, 2)}</span>` +
                    `<span>${this.t('balance-credits')}</span>` +
                '</div>';
        }
    },
    mounted() {
        if (typeof ResizeObserver === 'undefined') return;
        this.resizeObserver = new ResizeObserver(entries => {
            this.isCompact = entries[0].contentRect.width < COMPACT_WIDTH;
        });
        this.resizeObserver.observe(this.$el);
    },
    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
        removeTooltip(TOOLTIP_ID);
    }
};
</script>

<style scoped lang="scss">
.chart-title {
    font-size: 20px;
    font-weight: 500;
    color: #111827;

    @media (max-width: 576px) {
        font-size: 18px;
        text-align: center;
    }
}

.chart-content-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    width: 100%;

}

.chart-donut-wrapper {
    flex-shrink: 0;
    width: 150px;
    height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    margin: 0;
}

.chart-lateral-legend {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 150px;
    max-width: 290px;
    overflow-y: auto;
    padding-right: 6px;
    min-width: 0;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #D1D5DB;
        border-radius: 4px;

        &:hover {
            background-color: #9CA3AF;
        }
    }
}

.legend-row {
    padding: 3px 6px;
    height: 28px;
    font-size: 12px;
    transition: background 0.15s ease;
    border-radius: 6px;
    flex-shrink: 0;
    gap: 14px;

    &:hover {
        background-color: #F9FAFB;
    }
}

.legend-info {
    gap: 8px;
    min-width: 0;
    flex: 1;

    ::v-deep .kv-tooltip-container {
        display: block;
        min-width: 0;
        overflow: hidden;
    }
}

.legend-label-tooltip {
    min-width: 0;
    flex: 1;
    overflow: hidden;
}

.legend-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.legend-label {
    font-weight: 500;
    color: #374151;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
    cursor: default;
}

.legend-metrics {
    gap: 6px;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
}

.legend-percentage {
    font-family: "Sora", sans-serif;
    font-weight: 500;
    color: #111827;
    min-width: 44px;
    text-align: right;
    display: inline-block;
}

.chart-empty {
    min-height: 150px;
}

.is-compact {
    .chart-title {
        font-size: 18px;
    }

    .chart-content-row {
        flex-direction: column;
        gap: 16px;
    }

    .chart-lateral-legend {
        width: 100%;
        max-width: 100%;
        max-height: 220px;
        padding-left: 0 !important;
        padding-right: 0;
    }
}
</style>
